import {OrderIntakeProcessProvider} from '@hconnect/tuc-processes/orderIntake/OrderIntakeProcessProvider'
import React from 'react'

import {useCustomerById} from '../../../../Organisms/Invite/hooks/useCustomers'
import {OrderIntakeErrorState} from '../components/OrderIntakeErrorState'
import {OrderIntakeLoadingState} from '../components/OrderIntakeLoadingState'
import {JobSiteProvider} from '../contexts/JobSiteContext'
import {useOrderIntakeDefaultContacts} from '../hooks/useOrderIntakeDefaultContacts'
import {useOrderIntakeFeatureToggles} from '../hooks/useOrderIntakeFeatureToggles'
import {useOrderIntakeValidation} from '../hooks/useOrderIntakeValidation'
import {useRmcOrderIntakeSetup} from '../hooks/useRmcOrderIntakeSetup'

import {RmcOrderIntakeFlowContainer} from './RmcOrderIntakeFlowContainer'

/**
 * Component that sets up the OrderIntakeProcessProvider with RMC-specific configuration.
 * This integrates with the orderProcessesPackage to utilize its form management,
 * field hooks, and validation framework.
 */
export const RmcOrderIntakeProcessProvider: React.FC = () => {
  // Get all setup data (URL params, job site, options, etc.)
  const setup = useRmcOrderIntakeSetup()

  // Get feature toggles
  const featureToggles = useOrderIntakeFeatureToggles()

  // Build default contacts from persisted data and user profile
  const contacts = useOrderIntakeDefaultContacts(setup.persistedContact)

  // Fetch customer data by ID from job site
  const {
    data: customer,
    isLoading: isCustomerLoading,
    isError: isCustomerError
  } = useCustomerById(setup.jobSite?.customerId)

  // Validate prerequisites
  const validation = useOrderIntakeValidation(
    setup.jobSiteId,
    setup.jobSite,
    setup.options,
    setup.isOptionsError,
    setup.isOptionsLoading
  )

  // Handle error states
  if (validation.errorType) {
    return <OrderIntakeErrorState type={validation.errorType} />
  }

  // Handle customer error state
  if (isCustomerError) {
    return <OrderIntakeErrorState type="customerNotFound" />
  }

  // Handle loading states
  if (setup.isOptionsLoading || isCustomerLoading) {
    return <OrderIntakeLoadingState />
  }

  // At this point, we know jobSite exists due to validation
  const {jobSite, mappedOptions, isRepeatOrder, persistedOrders} = setup

  return (
    <JobSiteProvider jobSite={jobSite!}>
      <OrderIntakeProcessProvider
        orgUnitId={jobSite!.orgUnitId}
        orgUnitCountryId={jobSite!.country}
        customer={{
          customerId: customer?.customerId ?? jobSite!.customerId,
          confirmationPinRequired: customer?.confirmationPinRequired ?? false,
          customerName: customer?.customerName ?? '',
          customerNumber: customer?.customerNumber ?? jobSite!.customerNumber
        }}
        shippingAddress={{
          siteId: jobSite!.siteId,
          siteNumber: jobSite!.siteNumber,
          siteName: jobSite!.siteName,
          street: jobSite!.street,
          postalCode: jobSite!.postalCode,
          city: jobSite!.city,
          country: jobSite!.country
        }}
        contacts={contacts}
        orderIntakeType="create"
        options={mappedOptions}
        featureToggles={featureToggles}
      >
        <RmcOrderIntakeFlowContainer
          persistedOrders={isRepeatOrder ? persistedOrders : undefined}
        />
      </OrderIntakeProcessProvider>
    </JobSiteProvider>
  )
}
